/**
 * @packageDocumentation
 * @module hooks/useParentSize
 */
import { useCallback, useState } from 'react';
import { useResizeObserverRef } from '../useResizeObserver';
/**
 * `useParentSize` will provide width and height of the parent element containing the referenced element.
 * A `ResizeObserver` is used to watch for resize events and update the provided dimensions.
 *
 * @returns A tuple with a `callback ref` and the current `Dimensions` value.
 *
 * @example
 * ```jsx
 * function MyC() {
 *   const [ref, size] = useParentSize();
 *   return <div ref={ref}>
 *    { size && <Table {...size} />}
 *   </div>;
 * }
 * ```
 */
export function useParentSize(props = {}) {
  const [dimensions, setDimensions] = useState(props.initialDimensions);
  const [setParentRef] = useResizeObserverRef(entry => {
    const {
      ignoreHeight,
      ignoreWidth
    } = props;
    const parentNode = entry.target;
    if (parentNode) {
      const offsetHeight = parentNode.offsetHeight;
      const offsetWidth = parentNode.offsetWidth;
      const style = window.getComputedStyle(parentNode) || {};
      const styleHeight = parseFloat(style.height);
      const styleWidth = parseFloat(style.width);
      const deltaHeight = Math.abs(styleHeight - offsetHeight);
      const deltaWidth = Math.abs(styleWidth - offsetWidth);

      // in some cases, such as snippet of "table in windowshade" shown, the style.height and style.width may keep changed,
      // so compare both of sizes, from element and style, to get a value with decimal.
      let height = deltaHeight > 1 || isNaN(deltaHeight) ? offsetHeight : styleHeight;
      let width = deltaWidth > 1 || isNaN(deltaWidth) ? offsetWidth : styleWidth;
      width -= parseInt(style.paddingLeft, 10) || 0;
      width -= parseInt(style.paddingRight, 10) || 0;
      height -= parseInt(style.paddingTop, 10) || 0;
      height -= parseInt(style.paddingBottom, 10) || 0;
      if (!ignoreHeight && dimensions?.height !== height || !ignoreWidth && dimensions?.width !== width) {
        setDimensions({
          height,
          width
        });
      }
    }
  });
  const setRef = useCallback(element => {
    const parentElement = element?.parentElement ?? null;
    setParentRef(parentElement);
  }, [setParentRef]);
  return [setRef, dimensions];
}
//# sourceMappingURL=useParentSize.js.map